import React, { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { updateTaskAtom, createTaskAtom, deleteTaskAtom } from '../../../../atoms/v0/taskAtoms';
import { projectsAtom } from '../../../../atoms/v0/projectAtoms';
import { newTaskProjectIdAtom } from '../../../../atoms/v3/taskAtoms';
import { addSeconds, parseISO, startOfDay, addDays, subDays } from 'date-fns';
import { Task, Reminder, CreateTaskData, UpdateTaskData } from '../../../../types/task';
import { trackEvent } from '../../../../lib/analytics';
import { trackFormEvents, trackTaskEvents } from '../../../../lib/analyticsEnhanced';
import { logger } from '../../../../lib/logger';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import {
  IOSFormGroup,
  IOSFormItem,
  IOSFormLabel,
  IOSFormToggle,
  IOSFormInput,
  IOSFormTextarea,
  IOSFormSelect
} from '../ios/IOSFormGroup';
import { ImportanceCategory, TimeGroupCategory } from './DroppableArea';
import { v4 as uuidv4 } from 'uuid';
import { TaskPropertyTags } from './TaskPropertyTags';
import { TaskTimeEditor } from './TaskTimeEditor';
import { TaskReminderEditor } from './TaskReminderEditor';
import { TaskRecurrenceEditor } from './TaskRecurrenceEditor';
import { TaskImportanceEditor } from './TaskImportanceEditor';

interface TaskFormProps {
  taskId?: string | null; // 如果为null或undefined，则为新建任务
  projectId?: string | undefined; // 预选项目ID
  importance?: ImportanceCategory; // 预设重要性
  timeGroup?: TimeGroupCategory; // 预设时间组
  initialData?: Partial<CreateTaskData>; // 初始数据（用于长按创建任务）
  onClose: () => void;
  onSave?: () => void;
  tasks: Task[]; // 通过props传递任务列表，避免循环依赖
}

// 计算结束时间
const calculateEndAt = (start: string, costInSeconds: number): string => {
  try {
    const startDate = parseISO(start);
    return addSeconds(startDate, costInSeconds).toISOString();
  } catch (error) {
    console.error("Error calculating end date:", error);
    return ''; // Return empty string or handle error appropriately
  }
};

/**
 * 统一的任务表单组件，用于查看、编辑和新建任务
 * 参考iOS Calendar.app的设计风格
 */
export const TaskForm = React.forwardRef<{ handleSave: () => Promise<void> }, TaskFormProps>(
  ({ taskId, projectId, importance, timeGroup, initialData, onClose, onSave, tasks }, ref) => {
  const { t } = useTranslation(['v3/tasks', 'common']);
  const toast = useToast();
  const [projects] = useAtom(projectsAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [, createTask] = useAtom(createTaskAtom);
  const [, deleteTask] = useAtom(deleteTaskAtom);
  const [newTaskProjectId, setNewTaskProjectId] = useAtom(newTaskProjectIdAtom);
  const formInstanceId = React.useRef(uuidv4().substring(0, 8)).current;
  const titleInputRef = React.useRef<HTMLInputElement>(null);

  // 查找任务和项目
  const task = taskId ? tasks.find(t => t.id === taskId) : null;

  // 表单状态
  const [formData, setFormData] = useState<CreateTaskData | UpdateTaskData>(() => {
    if (task) {
      // 计算结束时间（如果有开始时间和时长）
      let endAt;
      if (task.startAt && task.cost) {
        endAt = calculateEndAt(task.startAt, task.cost);
      }

      return {
        id: task.id,
        title: task.title,
        notes: task.notes || '',
        done: task.done,
        vital: task.vital || false,
        urgent: task.urgent || false,
        fixed: task.fixed || false,
        projectId: task.projectId,
        startAt: task.startAt || undefined,
        cost: task.cost || undefined,
        endAt: endAt,
        dueAt: task.dueAt || undefined,
        reminders: task.reminders || [],
      } as UpdateTaskData;
    } else {
      return {
        title: '',
        notes: '',
        done: false,
        vital: false,
        urgent: false,
        fixed: false,
        projectId: undefined,
        startAt: undefined,
        cost: undefined,
        endAt: undefined,
        dueAt: undefined,
        reminders: [],
      } as CreateTaskData;
    }
  });

  // 弹窗状态管理
  const [isTimeEditorOpen, setIsTimeEditorOpen] = useState(false);
  const [isReminderEditorOpen, setIsReminderEditorOpen] = useState(false);
  const [isRecurrenceEditorOpen, setIsRecurrenceEditorOpen] = useState(false);
  const [isImportanceEditorOpen, setIsImportanceEditorOpen] = useState(false);

  // 根据时间组获取开始时间
  const getStartTimeFromTimeGroup = (timeGroup?: TimeGroupCategory): string | undefined => {
    if (!timeGroup) return undefined;

    const now = new Date();
    let date;

    switch (timeGroup) {
      case 'today':
        date = startOfDay(now);
        break;
      case 'tomorrow':
        date = startOfDay(addDays(now, 1));
        break;
      case 'yesterday':
        date = startOfDay(subDays(now, 1));
        break;
      default:
        return undefined;
    }

    return date.toISOString();
  };

  // 初始化表单状态
  useEffect(() => {
    logger.info(`TaskForm [${formInstanceId}] useEffect: Initializing form`, {
      component: 'TaskForm',
      instance: formInstanceId,
      effectHook: 'useEffect[]',
      taskId,
      hasTaskObject: !!task,
      initialProjectIdProp: projectId,
      initialImportanceProp: importance,
      initialTimeGroupProp: timeGroup,
      hasInitialData: !!initialData,
      jotaiNewTaskProjectId: newTaskProjectId
    });

    if (task) {
      // 编辑现有任务
      // 计算结束时间（如果有开始时间和时长）
      let endAt;
      if (task.startAt && task.cost) {
        endAt = calculateEndAt(task.startAt, task.cost);
      }

      const initialFormState = {
        id: task.id,
        title: task.title,
        notes: task.notes || '',
        done: task.done,
        vital: task.vital || false,
        urgent: task.urgent || false,
        fixed: task.fixed || false,
        projectId: task.projectId,
        startAt: task.startAt || undefined,
        cost: task.cost || undefined,
        endAt: endAt,
        dueAt: task.dueAt || undefined,
        reminders: task.reminders || [],
        recurrenceRule: task.recurrenceRule || undefined,
        isHabit: task.isHabit || false,
        // recurrenceGroupId 由系统自动管理，前端不需要传递
      } as UpdateTaskData;
      setFormData(initialFormState);
      logger.info(`TaskForm [${formInstanceId}] useEffect: Initialized for EXISTING task`, { instance: formInstanceId, taskId, initialTitle: task.title, formData: initialFormState });

    } else {
      // 创建新任务 - 优先使用initialData，其次使用传入的参数和Jotai原子中的预选项目ID
      if (initialData) {
        // 使用传入的初始数据（长按创建任务）
        const initialFormState = {
          title: initialData.title || '',
          notes: initialData.notes || '',
          done: initialData.done || false,
          vital: initialData.vital || false,
          urgent: initialData.urgent || false,
          fixed: initialData.fixed || false,
          projectId: initialData.projectId || projectId || newTaskProjectId || undefined,
          startAt: initialData.startAt || undefined,
          cost: initialData.cost || undefined,
          endAt: initialData.startAt && initialData.cost ? calculateEndAt(initialData.startAt, initialData.cost) : undefined,
          dueAt: initialData.dueAt || undefined,
          reminders: initialData.reminders || [],
          recurrenceRule: initialData.recurrenceRule || undefined,
          isHabit: initialData.isHabit || false,
          // recurrenceGroupId 由系统自动管理，前端不需要传递
        } as CreateTaskData;
        setFormData(initialFormState);
        logger.info(`TaskForm [${formInstanceId}] useEffect: Initialized for NEW task with initialData`, {
          instance: formInstanceId,
          initialData,
          formData: initialFormState
        });


      } else {
        // 使用传入的参数和Jotai原子中的预选项目ID
        const initialProjectId = projectId !== undefined ? projectId : (newTaskProjectId || undefined);

        // 根据传入的重要性参数设置vital和urgent
        const initialVital = importance?.vital || false;
        const initialUrgent = importance?.urgent || false;

        // 根据传入的时间组参数设置startAt
        const initialStartAt = getStartTimeFromTimeGroup(timeGroup);

        const initialFormState = {
          title: '',
          notes: '',
          done: false,
          vital: initialVital,
          urgent: initialUrgent,
          fixed: false,
          projectId: initialProjectId,
          startAt: initialStartAt,
          cost: undefined,
          endAt: undefined,
          dueAt: undefined,
          reminders: [],
          recurrenceRule: undefined,
          isHabit: false,
          // recurrenceGroupId 由系统自动管理，前端不需要传递
        } as CreateTaskData;
        setFormData(initialFormState);
        logger.info(`TaskForm [${formInstanceId}] useEffect: Initialized for NEW task`, {
          instance: formInstanceId,
          initialProjectId: initialProjectId,
          initialVital,
          initialUrgent,
          initialStartAt,
          formData: initialFormState
        });
      }

      // 使用后清除预选项目ID
      if (newTaskProjectId) {
        setNewTaskProjectId(undefined);
      }
    }
  }, [task, projectId, importance, timeGroup, initialData, newTaskProjectId, setNewTaskProjectId]);

  // 自动聚焦到标题输入框
  useEffect(() => {
    // 记录表单打开事件
    const formMode = task ? 'edit' : 'create';
    trackFormEvents.formOpened('task_form', formMode, {
      task_id: task?.id,
      has_initial_data: !!initialData,
      preset_project: !!projectId,
      preset_importance: !!importance,
      preset_time_group: !!timeGroup
    });

    // 延迟聚焦，确保组件完全渲染后再聚焦
    const timer = setTimeout(() => {
      if (titleInputRef.current) {
        titleInputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []); // 只在组件挂载时执行一次

  // 处理表单字段变更
  const handleChange = (field: string, value: any) => {
    setFormData(prev => {
      const newState = { ...prev, [field]: value };
      if (field === 'title') {
        logger.info(`TaskForm [${formInstanceId}] handleChange: Title changed`, { instance: formInstanceId, newTitle: value, oldTitle: prev.title });
      }

      // 记录表单字段编辑事件
      trackFormEvents.formFieldEdited('task_form', field, {
        task_id: task?.id,
        field_type: typeof value,
        has_value: !!value,
        value_length: typeof value === 'string' ? value.length : undefined
      });

      return newState;
    });
  };



  // 处理保存
  const handleSave = async () => {
    logger.info(`TaskForm [${formInstanceId}] handleSave: Attempting to save`, { instance: formInstanceId, currentFormData: formData });

    // 验证标题不能为空
    const currentTitle = formData.title;
    const trimmedTitle = currentTitle?.trim();
    logger.info(`TaskForm [${formInstanceId}] handleSave: Title validation`, { instance: formInstanceId, originalTitle: currentTitle, trimmedTitle });

    if (!trimmedTitle) {
      logger.warn(`TaskForm [${formInstanceId}] handleSave: Title is empty after trim. Save aborted`, { instance: formInstanceId, title: currentTitle });
      toast({
        title: t('v3/tasks:form.errors.emptyTitle'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    // 准备提交数据，移除虚拟字段endAt，确保title有值，并将undefined值转换为null
    const { endAt, ...dataToSubmitBase } = formData;
    const dataToSubmit = {
      ...dataToSubmitBase,
      title: trimmedTitle, // 使用 trim()后的标题
      startAt: dataToSubmitBase.startAt === undefined ? null : dataToSubmitBase.startAt,
      cost: dataToSubmitBase.cost === undefined ? null : dataToSubmitBase.cost,
      dueAt: dataToSubmitBase.dueAt === undefined ? null : dataToSubmitBase.dueAt
    };

    logger.info(`TaskForm [${formInstanceId}] handleSave: Data prepared for submission`, { instance: formInstanceId, dataToSubmit });

    // 立即关闭模态框，实现乐观UI
    if (onSave) {
      onSave();
    }
    onClose();

    try {
      if (task) {
        // 记录任务更新尝试事件
        logger.info(`TaskForm [${formInstanceId}] handleSave: Calling updateTask (via trackEvent)`, { instance: formInstanceId, taskId: task.id, data: dataToSubmit });
        trackEvent('task update attempt', {
          task_id: task.id,
          task_title: trimmedTitle.substring(0, 50),
          has_due_date: !!formData.dueAt,
          has_project: !!formData.projectId,
          has_reminders: !!(formData.reminders && formData.reminders.length > 0)
        });

        // 更新任务
        const success = await updateTask(dataToSubmit as UpdateTaskData);

        if (success) {
          logger.info(`TaskForm [${formInstanceId}] handleSave: updateTask successful (via trackEvent)`, { instance: formInstanceId, taskId: task.id });
          trackEvent('task updated', {
            task_id: task.id,
            task_title: trimmedTitle.substring(0, 50),
            has_due_date: !!formData.dueAt,
            has_project: !!formData.projectId,
            has_reminders: !!(formData.reminders && formData.reminders.length > 0)
          });

          // 显示成功提示
          toast({
            title: t('v3/tasks:toast.updateSuccess'),
            description: trimmedTitle,
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        } else {
          logger.warn(`TaskForm [${formInstanceId}] handleSave: updateTask returned false`, { instance: formInstanceId, taskId: task.id });

          // 显示失败提示
          toast({
            title: t('v3/tasks:toast.updateFailed'),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        }
      } else {
        // 记录任务创建尝试事件
        logger.info(`TaskForm [${formInstanceId}] handleSave: Calling createTask (via trackEvent)`, { instance: formInstanceId, data: dataToSubmit });
        trackEvent('task create attempt', {
          task_title: trimmedTitle.substring(0, 50),
          has_due_date: !!formData.dueAt,
          has_project: !!formData.projectId,
          has_reminders: !!(formData.reminders && formData.reminders.length > 0),
          is_vital: !!formData.vital,
          is_urgent: !!formData.urgent
        });

        // 创建任务
        const success = await createTask(dataToSubmit as CreateTaskData);

        if (success) {
          logger.info(`TaskForm [${formInstanceId}] handleSave: createTask successful (via trackEvent)`, { instance: formInstanceId, title: trimmedTitle });
          trackEvent('task created', {
            task_title: trimmedTitle.substring(0, 50),
            has_due_date: !!formData.dueAt,
            has_project: !!formData.projectId,
            has_reminders: !!(formData.reminders && formData.reminders.length > 0),
            is_vital: !!formData.vital,
            is_urgent: !!formData.urgent
          });

          // 显示成功提示
          toast({
            title: t('v3/tasks:toast.createSuccess'),
            description: trimmedTitle,
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        } else {
          logger.warn(`TaskForm [${formInstanceId}] handleSave: createTask returned false`, { instance: formInstanceId, title: trimmedTitle });

          // 显示失败提示
          toast({
            title: t('v3/tasks:toast.createFailed'),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        }
      }
    } catch (error) {
      logger.error(`TaskForm [${formInstanceId}] handleSave: Error during save operation`, error instanceof Error ? error : new Error(String(error)), { instance: formInstanceId, taskId: task?.id, title: trimmedTitle });
      // 记录任务保存失败事件
      trackEvent('task save failed', {
        is_new: !task,
        task_title: (formData.title || '无标题').substring(0, 50), // Linter fix: Handle possible undefined title
        error: error instanceof Error ? error.message : String(error)
      });

      console.error('保存任务失败:', error);

      // 显示错误提示
      toast({
        title: task ? t('v3/tasks:toast.updateFailed') : t('v3/tasks:toast.createFailed'),
        description: error instanceof Error ? error.message : String(error),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  // 暴露方法给父组件
  React.useImperativeHandle(ref, () => ({
    handleSave
  }));



  // 处理删除任务
  const handleDeleteTask = async () => {
    if (!task) return;
    logger.info(`TaskForm [${formInstanceId}] handleDeleteTask: Confirming delete`, { instance: formInstanceId, taskId: task.id, taskTitle: task.title });

    // 记录任务删除确认事件
    trackEvent('task delete confirm', {
      task_id: task.id,
      task_title: task.title.substring(0, 50) // 只记录前50个字符
    });

    if (confirm('确定要删除此任务吗？此操作不可撤销。')) {
      logger.info(`TaskForm [${formInstanceId}] handleDeleteTask: Delete confirmed, attempting delete (via trackEvent)`, { instance: formInstanceId, taskId: task.id });
      // 记录任务删除尝试事件
      trackEvent('task delete attempt', {
        task_id: task.id,
        task_title: task.title.substring(0, 50) // 只记录前50个字符
      });

      // 立即关闭模态框，实现乐观UI
      onClose();

      try {
        const success = await deleteTask(task.id);

        if (success) {
          logger.info(`TaskForm [${formInstanceId}] handleDeleteTask: Delete successful (via trackEvent)`, { instance: formInstanceId, taskId: task.id });
          // 记录任务删除成功事件
          trackEvent('task deleted', {
            task_id: task.id,
            task_title: task.title.substring(0, 50) // 只记录前50个字符
          });

          // 显示成功提示
          toast({
            title: t('v3/tasks:toast.deleteSuccess'),
            description: task.title,
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        } else {
          logger.warn(`TaskForm [${formInstanceId}] handleDeleteTask: deleteTask returned false`, { instance: formInstanceId, taskId: task.id });

          // 显示失败提示
          toast({
            title: t('v3/tasks:toast.deleteFailed'),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        }
      } catch (error) {
        logger.error(`TaskForm [${formInstanceId}] handleDeleteTask: Error during delete`, error instanceof Error ? error : new Error(String(error)), { instance: formInstanceId, taskId: task.id });
        // 记录任务删除失败事件
        trackEvent('task delete failed', {
          task_id: task.id,
          task_title: task.title.substring(0, 50), // 只记录前50个字符
          error: error instanceof Error ? error.message : String(error)
        });

        console.error('删除任务失败:', error);

        // 显示错误提示
        toast({
          title: t('v3/tasks:toast.deleteFailed'),
          description: error instanceof Error ? error.message : String(error),
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top'
        });
      }
    }
  };

  // 弹窗处理函数
  const handleTimeEditorSave = async (data: { startAt?: string; cost?: number; dueAt?: string }) => {
    // 如果是编辑模式，直接提交到后端
    if (task) {
      try {
        const updateData = {
          id: task.id,
          startAt: data.startAt === undefined ? null : data.startAt,
          cost: data.cost === undefined ? null : data.cost,
          dueAt: data.dueAt === undefined ? null : data.dueAt
        };

        const success = await updateTask(updateData as UpdateTaskData);
        if (success) {
          toast({
            title: t('v3/tasks:toast.timeUpdateSuccess'),
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top'
          });
        } else {
          toast({
            title: t('v3/tasks:toast.timeUpdateFailed'),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        }
      } catch (error) {
        console.error('时间设置保存失败:', error);
        toast({
          title: t('v3/tasks:toast.timeUpdateFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      }
    } else {
      // 新建模式，更新本地状态
      setFormData(prev => ({
        ...prev,
        startAt: data.startAt,
        cost: data.cost,
        dueAt: data.dueAt,
        // 重新计算结束时间
        endAt: data.startAt && data.cost ? calculateEndAt(data.startAt, data.cost) : undefined
      }));
    }
  };

  const handleReminderEditorSave = async (reminders: Reminder[]) => {
    // 如果是编辑模式，直接提交到后端
    if (task) {
      try {
        const updateData = {
          id: task.id,
          reminders: reminders
        };

        const success = await updateTask(updateData as UpdateTaskData);
        if (success) {
          toast({
            title: t('v3/tasks:toast.reminderUpdateSuccess'),
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top'
          });
        } else {
          toast({
            title: t('v3/tasks:toast.reminderUpdateFailed'),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        }
      } catch (error) {
        console.error('提醒设置保存失败:', error);
        toast({
          title: t('v3/tasks:toast.reminderUpdateFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      }
    } else {
      // 新建模式，更新本地状态
      handleChange('reminders', reminders);
    }
  };

  const handleRecurrenceEditorSave = async (data: { recurrenceRule?: string; isHabit?: boolean }) => {
    // 如果是编辑模式，直接提交到后端
    if (task) {
      try {
        const updateData = {
          id: task.id,
          recurrenceRule: data.recurrenceRule === undefined ? null : data.recurrenceRule,
          isHabit: data.isHabit
        };

        const success = await updateTask(updateData as UpdateTaskData);
        if (success) {
          toast({
            title: t('v3/tasks:toast.recurrenceUpdateSuccess'),
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top'
          });
        } else {
          toast({
            title: t('v3/tasks:toast.recurrenceUpdateFailed'),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        }
      } catch (error) {
        console.error('重复设置保存失败:', error);
        toast({
          title: t('v3/tasks:toast.recurrenceUpdateFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      }
    } else {
      // 新建模式，更新本地状态
      handleChange('recurrenceRule', data.recurrenceRule);
      handleChange('isHabit', data.isHabit);
    }
  };

  const handleImportanceEditorSave = async (data: { vital?: boolean; urgent?: boolean; fixed?: boolean }) => {
    // 如果是编辑模式，直接提交到后端
    if (task) {
      try {
        const updateData = {
          id: task.id,
          vital: data.vital,
          urgent: data.urgent,
          fixed: data.fixed
        };

        const success = await updateTask(updateData as UpdateTaskData);
        if (success) {
          toast({
            title: t('v3/tasks:toast.importanceUpdateSuccess'),
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top'
          });
        } else {
          toast({
            title: t('v3/tasks:toast.importanceUpdateFailed'),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        }
      } catch (error) {
        console.error('重要性设置保存失败:', error);
        toast({
          title: t('v3/tasks:toast.importanceUpdateFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      }
    } else {
      // 新建模式，更新本地状态
      handleChange('vital', data.vital);
      handleChange('urgent', data.urgent);
      handleChange('fixed', data.fixed);
    }
  };

  return (
    <div className="font-ios pb-20">
      {/* 新布局：完成状态 + 标题 */}
      <IOSFormGroup>
        <IOSFormItem>
          <div className="flex items-center gap-3">
            {/* 完成状态 - 移到标题前面 */}
            {task && (
              <IOSFormToggle
                checked={!!formData.done}
                onChange={(checked) => handleChange('done', checked)}
              />
            )}
            {/* 标题输入 */}
            <IOSFormInput
              ref={titleInputRef}
              value={formData.title || ''}
              onChange={(value) => handleChange('title', value)}
              placeholder={t('v3/tasks:form.titlePlaceholder')}
              className="text-ios-title3 font-medium flex-1"
            />
          </div>
        </IOSFormItem>
      </IOSFormGroup>

      {/* 项目选择 */}
      <IOSFormGroup>
        <IOSFormItem className="flex items-center justify-between">
          <IOSFormLabel className="whitespace-nowrap">{t('common:projects')}</IOSFormLabel>
          <IOSFormSelect
            value={formData.projectId || ''}
            onChange={(value) => handleChange('projectId', value || undefined)}
            options={projects.map(project => ({ value: project.id, label: project.title }))}
            placeholder={t('v3/tasks:form.noProject')}
          />
        </IOSFormItem>
      </IOSFormGroup>

      {/* 备注 - 改为自适应高度 */}
      <IOSFormGroup>
        <IOSFormItem>
          <IOSFormLabel className="block mb-2">{t('v3/tasks:form.notes')}</IOSFormLabel>
          <IOSFormTextarea
            value={formData.notes || ''}
            onChange={(value) => handleChange('notes', value)}
            placeholder={t('v3/tasks:form.notesPlaceholder')}
            rows={2}
            className="min-h-[60px] resize-none"
            maxHeightMode="viewport"
          />
        </IOSFormItem>
      </IOSFormGroup>

      {/* 功能标签横向滚动区域 */}
      <IOSFormGroup>
        <IOSFormItem>
          <TaskPropertyTags
            task={task}
            formData={formData}
            onTimeClick={() => {
              trackFormEvents.editorOpened('time_editor', { task_id: task?.id });
              setIsTimeEditorOpen(true);
            }}
            onReminderClick={() => {
              trackFormEvents.editorOpened('reminder_editor', { task_id: task?.id });
              setIsReminderEditorOpen(true);
            }}
            onRecurrenceClick={() => {
              trackFormEvents.editorOpened('recurrence_editor', { task_id: task?.id });
              setIsRecurrenceEditorOpen(true);
            }}
            onImportanceClick={() => {
              trackFormEvents.editorOpened('importance_editor', { task_id: task?.id });
              setIsImportanceEditorOpen(true);
            }}
            onDeleteClick={task ? handleDeleteTask : undefined}
          />
        </IOSFormItem>
      </IOSFormGroup>

      {/* 弹窗组件 */}
      <TaskTimeEditor
        isOpen={isTimeEditorOpen}
        onClose={() => setIsTimeEditorOpen(false)}
        startAt={formData.startAt}
        cost={formData.cost}
        dueAt={formData.dueAt}
        onSave={handleTimeEditorSave}
      />

      <TaskReminderEditor
        isOpen={isReminderEditorOpen}
        onClose={() => setIsReminderEditorOpen(false)}
        reminders={formData.reminders || []}
        onSave={handleReminderEditorSave}
        taskTimes={{
          startAt: formData.startAt,
          dueAt: formData.dueAt,
          cost: formData.cost
        }}
      />

      <TaskRecurrenceEditor
        isOpen={isRecurrenceEditorOpen}
        onClose={() => setIsRecurrenceEditorOpen(false)}
        recurrenceRule={formData.recurrenceRule}
        isHabit={formData.isHabit}
        onSave={handleRecurrenceEditorSave}
      />

      <TaskImportanceEditor
        isOpen={isImportanceEditorOpen}
        onClose={() => setIsImportanceEditorOpen(false)}
        vital={formData.vital}
        urgent={formData.urgent}
        fixed={formData.fixed}
        onSave={handleImportanceEditorSave}
      />
    </div>
  );
});
